import allCards		from '../data/AllCards.json';
import skillList	from '../data/skills.json';

import {
	profilePic as characterProfiles
} from './characters';

import {
	cloneDeep			as _cloneDeep,
	isPlainObject as _isPlainObject
}									from 'lodash';

const cardList = _cloneDeep(allCards);
const MAX_STATS = {
	atk: 0,
	hp: 0,
	def: 0
};

const RANK_0_KEY = 'r0';
const RANK_5_KEY = 'r5';

const store = {
	getCardKey(card, rankKey) {
		return `owned:${card.id}:${rankKey}`;
	},
	getCardValue(card, rankKey) {
		const key = store.getCardKey(card, rankKey);
		return parseInt(localStorage.getItem(key)) || 0;
	},
	addCard(card) {
		for (const rankKey of [ RANK_0_KEY, RANK_5_KEY ]) {
			const key = store.getCardKey(card, rankKey);
			if (card.owned[rankKey])
				localStorage.setItem(key, card.owned[rankKey]);
			else
				localStorage.removeItem(key);
		}
	}
};
const cardsByID = {};

// SETUP
for (const i in cardList) {
	const card = cardList[i];
	cardsByID[card.id] = card;
	card.owned = {
		[RANK_0_KEY]: store.getCardValue(card, 'r0'),
		[RANK_5_KEY]: store.getCardValue(card, 'r5')
	};
	let isCharacterCard = null;
	if (Object.keys(card.stats ?? {}).length) {
		for (const key in MAX_STATS) {
			if (typeof(card.stats[key]) === 'number' && card.stats[key] > MAX_STATS[key])
				MAX_STATS[key] = card.stats[key];
		}
	}
	for (const skill of card.skills) {
		const skillDef = skillList[skill.name];
		if (!skillDef) {
			console.warn('card setup: Skill not defined:', skill.name);
			continue;
		}
		skill.def = skillDef;
		skill.icon = skillDef.icon;
		if (typeof(skill.requirement) == 'string') { // character
			if (skillDef.type !== 'character') {
				console.warn('card setup: incompatible skill type', card, skillDef);
				continue;
			}
			const overrideSettings = skillDef[skill.requirement];
			skill.description = skillDef.I;
			if (overrideSettings) {
				if (overrideSettings.I)
					skill.description = overrideSettings.I;
				if (overrideSettings.icon)
					skill.icon = overrideSettings.icon;
			}
			skill.label = `${skill.name} I`;
			isCharacterCard = skill.requirement;
		} else if (_isPlainObject(skill.requirement)) {
			if (Object.keys(skill.requirement).find(type => ![ 'R', 'G', 'B', 'Y' ].includes(type))) {
				console.warn('card setup: skill requirement badly defined: only RGBY keys are allowed', card);
				continue;
			} else if (!Object.keys(skill.requirement).length) {
				console.warn('card setup: skill requirement badly defined: missing RGBY key restriction', card);
				continue;
			}
			const skillRank = card.rank === 2 ? 'II' : 'I';
			skill.label = `${skill.name}`;
			if (!(skillRank in skillDef)) {
				if (skillDef.description)
					skill.description = skillDef.description;
				else if (skillRank === 'II' && skillDef.I) {
					skill.label += ' I';
					skill.description = skillDef.I;
				} else {
					console.warn('card setup: skill rank not found', card, skill, skillDef);
					continue;
				}
			} else {
				skill.label += ` ${skillRank}`;
				skill.description = skillDef[skillRank];
			}
			// TMP solution
			skill.requirement.toString = Object.entries(skill.requirement)
				.reduce((str, [ type, amount ]) => str + type.repeat(amount), '');
		} else {
			console.warn('card setup: incompatible skill requirement declaration', card);
			continue;
		}
		if (skillDef.unique) {
			if (skillDef.unique === 'rank')
				skill.description += ' When you equip more than 1 copy of a card, only the card with higher star rank will be activated.';
			else
				skill.description += ' When you equip more than 1 copy of this card, only the card in the right most slot will be activated.';
		}
	}
	if (characterProfiles[isCharacterCard])
		card.background = characterProfiles[isCharacterCard];
}
Object.freeze(cardsByID);

const SORT_FUNCTIONS = {
	id: (a, b) => a.id - b.id,
	common(key) {
		return (a, b) => {
			return a[key] - b[key] || a.id - b.id;
		};
	}
};
const FILTERS_FUNCTIONS = {
	owned(card, filters) {
		for (const v of filters.owned) {
			if (card.owned[v] > 0)
				return true;
		}
		return false;
	},
	name(card, { name: nameSearch }) {
		nameSearch = nameSearch
			.trim()
			.replace(/([^\s]+)\s?/g, '.*$1')
			+ '.*';
		return card.name?.toString().match(new RegExp(nameSearch, 'i'));
	},
	skills(card, { skills }) {
		if (skills) {
			for (const skill of skills) {
				if (skill.startsWith('character-')) {
					const charName = skill.replace('character-', '');
					if (card.skills.find(s => s.requirement === charName))
						return true;
				} else {
					if (card.skills.find(s => s.name === skill))
						return true;
				}
			}
			return false;
		}
		return true;
	},
	require(card, { require: requiredComb }) {
		if (requiredComb) {
			for (const skill of card.skills) {
				const sReq = skill.requirement;
				if (_isPlainObject(sReq)) {
					const cardTypeKey = [ 'R', 'Y', 'B', 'G' ][card.type];
					const typeKeys = requiredComb.map(v => [ 'R', 'Y', 'B', 'G' ][v])
						.reduce((keys, key) => {
							keys[key] = (keys[key] ?? 0) + 1;
							return keys;
						}, {});
					let cantGoFurther = false;
					for (const key in sReq) {
						if (key === 'toString')
							continue;
						if ((cantGoFurther = (!(key in typeKeys) && (sReq[key] !== 1 || cardTypeKey !== key))))
							break;
						typeKeys[key] = (typeKeys[key] ?? 0) - sReq[key];
					}
					if (cantGoFurther)
						continue;
					for (const key in typeKeys) {
						if (!(typeKeys[key] === 0
							|| (typeKeys[key] === -1 && sReq[key] <= 2
							&& requiredComb.length < 3 && cardTypeKey === key))) {
							cantGoFurther = true;
							break;
						}
					}
					if (cantGoFurther)
						continue;
					return true;
				}
			}
			return false;
		}
		return true;
	},
	common(card, filters, filterField) {
		return filters[filterField].includes(card[filterField]);
	}
};

function getCard(id) {
	return _cloneDeep(cardsByID[id]);
}

function getCards(filters, from = 0, size = cardList.length) {
	// filter parsing
	const { sort } = filters;
	filters = { ...filters }; // only main structure will be modified below
	delete filters.sort;
	for (const filterName in filters) {
		const filter = filters[filterName];
		if (typeof(filter) == 'string' || Array.isArray(filter)) {
			if (!filter.length)
				delete filters[filterName];
		} else if (_isPlainObject(filter) && !Object.keys(filter).length)
			delete filters[filterName];
	}
	// parsing end

	let filteredCards = cardList;

	/* FILTERING */
	if (Object.keys(filters).length) { // has filter(s)
		filteredCards = filteredCards.filter(card => {
			for (const filterField in filters) {
				const f = FILTERS_FUNCTIONS?.[filterField] ?? FILTERS_FUNCTIONS.common;
				if (!f(card, filters, filterField))
					return false;
			}
			return true;
		});
	}
	/* SORTING */
	if (SORT_FUNCTIONS[sort])
		filteredCards.sort(SORT_FUNCTIONS[sort]);
	else
		filteredCards.sort(SORT_FUNCTIONS.common(sort));

	/* OUTPUT */
	return filteredCards.slice(from, from + size);
}

const saveCard = store.addCard;

Object.freeze(MAX_STATS);
export {
	RANK_0_KEY,
	RANK_5_KEY,

	MAX_STATS,

	getCard,
	getCards,
	saveCard
};
